import { useEffect, useRef, useState } from "react";
import { api, fmtErr } from "../lib/api";
import { toast } from "sonner";
import { UserSwitch, ArrowRight, CheckCircle, XCircle } from "@phosphor-icons/react";

export const MoveDetectorCard = () => {
  const [status, setStatus] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [busy, setBusy] = useState(null);
  const pollRef = useRef(null);

  const load = () => Promise.all([
    api.moveStatus().then(setStatus),
    api.moveProposals().then(d => setProposals(d.items)),
  ]).catch(() => {});

  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, []);

  useEffect(() => {
    clearInterval(pollRef.current);
    if (status?.run?.status === "running") pollRef.current = setInterval(load, 3000);
    return () => clearInterval(pollRef.current);
  }, [status?.run?.status]);

  if (!status) return null;
  const run = status.run;
  const running = run?.status === "running";

  const startRun = async () => {
    try {
      await api.moveRun(500);
      toast.success("Move check started — checking up to 500 contacts in the background");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const apply = async (p) => {
    setBusy(p.id);
    try {
      await api.moveApply(p.id);
      toast.success(`${p.name} marked as no longer at ${p.old_company || "their company"} — the Rescue tool below can find their new address`);
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(null); }
  };
  const dismiss = async (p) => {
    setBusy(p.id);
    try { await api.moveDismiss(p.id); load(); } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(null); }
  };

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="move-detector-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-heading text-base font-medium flex items-center gap-2">
            <UserSwitch size={16} className="text-violet-600"/> Who left? — free move check
          </h3>
          <p className="text-xs text-zinc-500 mt-0.5 max-w-2xl">
            Checks active contacts you've emailed in the last 90 days against Apollo (free searches — no credits).
            Anyone who's vanished from their company and turned up somewhere else is proposed here for review.
            Runs itself monthly; nothing changes without your approval.
          </p>
        </div>
        <div className="shrink-0">
          {running ? (
            <div className="text-xs text-zinc-600 rounded-md border border-zinc-200 px-3 py-2" data-testid="move-progress">
              <span className="animate-pulse">●</span> Checking… {run.checked || 0} of {run.total ?? "?"} · {run.movers || 0} mover{(run.movers || 0) === 1 ? "" : "s"} found
            </div>
          ) : (
            <button data-testid="move-run-btn" onClick={startRun} disabled={!status.apollo_configured}
              title={status.apollo_configured ? "" : "Apollo API key not configured"}
              className="h-9 px-4 rounded-md border border-zinc-200 bg-white text-sm font-medium text-zinc-700 hover:border-zinc-300 disabled:opacity-40">
              Run check now
            </button>
          )}
        </div>
      </div>

      {run && !running && (
        <div className="text-[11px] text-zinc-500 mt-2" data-testid="move-last-run">
          Last run {run.finished_at ? new Date(run.finished_at).toLocaleDateString() : "—"} ({run.trigger}) ·
          checked {run.checked || 0} · found {run.movers || 0} mover{(run.movers || 0) === 1 ? "" : "s"} ·
          {" "}{status.total_checked.toLocaleString()} contacts covered so far
          {run.error && <span className="text-amber-700"> · {run.error}</span>}
        </div>
      )}

      {proposals.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="move-proposals">
          {proposals.map(p => (
            <div key={p.id} className="flex items-center gap-3 rounded-md border border-violet-100 bg-violet-50/40 px-3 py-2" data-testid={`move-proposal-${p.contact_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-zinc-900 truncate">{p.name} <span className="font-normal text-zinc-400 text-xs">{p.email}</span></div>
                <div className="text-xs text-zinc-600 flex items-center gap-1.5 mt-0.5 flex-wrap">
                  <span className="truncate max-w-[180px]">{p.old_company || "—"}</span>
                  <ArrowRight size={11} className="text-violet-500 shrink-0"/>
                  <span className="font-medium truncate max-w-[220px]">{p.new_company || "a new company"}</span>
                  {p.new_title && <span className="text-zinc-400 truncate max-w-[200px]">· {p.new_title}</span>}
                  <span className="text-zinc-400">· checked {new Date(p.checked_at).toLocaleDateString()}</span>
                </div>
              </div>
              <button data-testid={`move-apply-${p.contact_id}`} onClick={() => apply(p)} disabled={busy === p.id}
                className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded border border-violet-300 text-violet-800 hover:bg-violet-100 disabled:opacity-50">
                <CheckCircle size={12}/> Mark as left
              </button>
              <button data-testid={`move-dismiss-${p.contact_id}`} onClick={() => dismiss(p)} disabled={busy === p.id}
                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded text-zinc-500 hover:text-zinc-900 disabled:opacity-50">
                <XCircle size={12}/> Dismiss
              </button>
            </div>
          ))}
        </div>
      )}
      {proposals.length === 0 && run?.status === "done" && (
        <div className="text-xs text-zinc-400 mt-3" data-testid="move-no-proposals">No unreviewed movers right now.</div>
      )}
    </div>
  );
};
